import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", category: "", description: "", image: "", distance: "", travel_time: "", google_maps_url: "", sort_order: 999 };

export default function AttractionsManager() {
  const [items, setItems] = useState([]);

  const load = useCallback(() => http.get("/admin/attractions").then((r) => setItems(r.data.attractions)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (a, patch) => {
    try {
      await http.put(`/admin/attractions/${a.id}`, {
        name: a.name, category: a.category || "", description: a.description || "", image: a.image || "",
        distance: a.distance || "", travel_time: a.travel_time || "", google_maps_url: a.google_maps_url || "",
        active: a.active ?? true, sort_order: a.sort_order ?? 999, ...patch,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const archive = async (id) => {
    try { await http.delete(`/admin/attractions/${id}`); toast.success("Card disabled"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="attractions-manager">
      <AddAttractionForm nextSortOrder={items.length} onAdded={load} />
      <div className="card">
        <h3 className="font-serif text-2xl mb-3">Explore Cards ({items.filter((i) => i.active).length} active)</h3>
        <div className="space-y-3">
          {items.map((a) => (
            <AttractionRow key={a.id} a={a} onSave={save} onArchive={archive} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AddAttractionForm({ nextSortOrder, onAdded }) {
  const [form, setForm] = useState(EMPTY);

  const add = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      await http.post("/admin/attractions", { ...form, sort_order: nextSortOrder });
      toast.success("Place added");
      setForm(EMPTY);
      onAdded();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="card">
      <h3 className="font-serif text-2xl mb-4">Add Place / Experience</h3>
      <div className="grid md:grid-cols-3 gap-3">
        <input data-testid="attr-form-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="attr-form-category" placeholder="Category (e.g. Temple · Sunset)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <input data-testid="attr-form-image" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
        <input data-testid="attr-form-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" />
        <input data-testid="attr-form-maps" placeholder="Google Maps link (optional)" value={form.google_maps_url} onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })} />
        <button data-testid="attr-form-add" onClick={add} className="btn-primary flex items-center justify-center gap-2 md:col-span-3"><Plus size={16} /> Add Card</button>
      </div>
    </div>
  );
}

function AttractionRow({ a, onSave, onArchive }) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`attr-row-${a.id}`}>
      {a.image && <img src={a.image} alt={a.name} className="w-16 h-12 rounded-lg object-cover" />}
      <div className="flex-1 min-w-40">
        <div className="font-medium">{a.name}{!a.active && <span className="badge-terracotta ml-2">disabled</span>}</div>
        <div className="text-xs" style={{ color: "var(--muted)" }}>{a.category}</div>
      </div>
      <button data-testid={`attr-rename-${a.id}`} onClick={() => {
        const name = window.prompt("Edit name", a.name);
        if (name && name !== a.name) onSave(a, { name });
      }} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>Rename</button>
      <button data-testid={`attr-edit-desc-${a.id}`} onClick={() => {
        const description = window.prompt("Edit description", a.description);
        if (description !== null) onSave(a, { description });
      }} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>Edit text</button>
      <button data-testid={`attr-toggle-${a.id}`} onClick={() => onSave(a, { active: !a.active })} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>
        {a.active ? "Disable" : "Enable"}
      </button>
      <button data-testid={`attr-archive-${a.id}`} onClick={() => onArchive(a.id)} className="p-2" style={{ color: "var(--terracotta)" }}><Trash2 size={16} /></button>
    </div>
  );
}
